import { useState } from 'react';
import { sendSubscribeWebhook } from '../../../utils/webhookUtils';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('submitting');

    try {
      const [res] = await Promise.allSettled([
        fetch('https://readdy.ai/api/form/d6t4n6vcrcujsk0v13d0', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ email, name, source: 'About Page Signup' }).toString(),
        }),
        sendSubscribeWebhook({
          email,
          name,
          source: 'About Page Signup',
          page_url: window.location.href,
        }),
      ]);

      const localOk = res.status === 'fulfilled' && (res.value as Response).ok;
      if (localOk) {
        setStatus('success');
        setEmail('');
        setName('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputBase =
    'w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-500/60 focus:shadow-[0_0_0_3px_rgba(6,182,212,0.08)] transition-all duration-200';

  return (
    <section className="py-14 relative overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute top-0 right-1/3 w-80 h-80 bg-purple-600/6 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-cyan-600/6 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-2xl mx-auto px-6 relative z-10">
        <div className="rounded-2xl border border-white/8 bg-gradient-to-b from-white/[0.02] to-transparent overflow-hidden">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />

          <div className="px-8 py-10">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-600/20 border border-cyan-500/20">
                <i className="ri-mail-send-line text-cyan-400 text-xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">Stay in the Loop</h2>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Get new reviews, buying guides, and deal alerts delivered to your inbox. No spam, ever.
                </p>
              </div>
            </div>

            {status === 'success' ? (
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
                  <i className="ri-checkbox-circle-fill text-emerald-400 text-3xl"></i>
                </div>
                <p className="text-emerald-300 text-base font-semibold">Thanks for subscribing. Check your email.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-1 px-5 py-2 border border-white/15 text-gray-400 hover:text-white hover:border-white/30 rounded-lg text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Subscribe another email
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="hidden" name="source" value="About Page Signup" />

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Name <span className="text-gray-600 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className={inputBase}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Email <span className="text-cyan-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className={inputBase}
                    />
                  </div>
                </div>

                {status === 'error' && (
                  <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/25 text-red-400 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-base"></i>
                    </div>
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3.5 bg-gradient-to-r from-cyan-600 to-emerald-600 text-white font-bold rounded-xl text-sm hover:shadow-xl hover:shadow-cyan-500/25 transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Subscribing...
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line"></i>
                      Subscribe to Newsletter
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-gray-600">No spam. Unsubscribe anytime. We respect your privacy.</p>
              </form>
            )}
          </div>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
        </div>
      </div>
    </section>
  );
}
